import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MailPlus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataList } from "@/components/common/DataList";
import { YesNoBadge } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, formatError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Field } from "../CompanyFormDialog";

function emailToast(log) {
  if (log.status === "sent") toast.success(`Invite sent to ${log.actual_to.join(", ")}${log.safe_mode ? " (safe mode)" : ""}`);
  else toast.error(`Invite email ${log.status}: ${log.error || ""}`);
}

function InviteDialog({ onClose, onDone }) {
  const { appSettings } = useAuth();
  const [form, setForm] = useState({ email: "", full_name: "", role: "payroll_officer" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users/invite", form);
      emailToast(data.email);
      onDone();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite a staff member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {appSettings?.email_safe_mode && (
            <p className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2" data-testid="invite-safe-mode-notice">
              SAFE MODE — the invitation email will go to {appSettings.safe_mode_recipient} only.
            </p>
          )}
          <Field label="Full name"><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="invite-fullname-input" /></Field>
          <Field label="Email"><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" /></Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="invite-send-button">Send invitation</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const load = useCallback(() => api.get("/admin/users").then((r) => { setUsers(r.data); setLoading(false); }), []);
  useEffect(() => { load(); }, [load]);

  const update = async (u, body) => {
    try { await api.put(`/admin/users/${u.id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const action = async (u, path, msg) => {
    try { const { data } = await api.post(`/admin/users/${u.id}/${path}`); data.email ? emailToast(data.email) : toast.success(msg); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const stop = (e) => e.stopPropagation();

  const columns = [
    { key: "full_name", label: "Name", render: (u) => <span className="font-medium text-slate-900">{u.full_name}{u.invite_pending && <span className="ml-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Invite pending</span>}</span> },
    { key: "email", label: "Email" },
    { key: "role", label: "Role", render: (u) => (
      <div onClick={stop}>
        <Select value={u.role} onValueChange={(v) => update(u, { role: v })} disabled={u.id === me.id}>
          <SelectTrigger className="h-8 w-40" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>) },
    { key: "mfa", label: "MFA", render: (u) => <YesNoBadge value={u.mfa_enabled} yes="On" no="Off" testId={`user-mfa-${u.id}`} /> },
    { key: "last", label: "Last sign-in", render: (u) => fmtDateTime(u.last_login_at) },
    { key: "active", label: "Active", render: (u) => <span onClick={stop}><Switch checked={u.active} disabled={u.id === me.id} onCheckedChange={(v) => update(u, { active: v })} data-testid={`user-active-switch-${u.id}`} /></span> },
    { key: "actions", label: "", render: (u) => (
      <div className="flex gap-1" onClick={stop}>
        {u.invite_pending && <Button size="sm" variant="ghost" onClick={() => action(u, "resend-invite")} data-testid={`user-resend-invite-${u.id}`}><MailPlus className="h-4 w-4 mr-1" />Resend</Button>}
        {u.mfa_enabled && <Button size="sm" variant="ghost" onClick={() => action(u, "reset-mfa", "MFA reset")} data-testid={`user-reset-mfa-${u.id}`}>Reset MFA</Button>}
      </div>) },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Roles: admins manage everything; payroll officers create and edit; read only can view.</p>
        <Button onClick={() => setInviting(true)} data-testid="invite-user-button"><UserPlus className="h-4 w-4 mr-1" /> Invite user</Button>
      </div>
      <DataList columns={columns} rows={users} loading={loading} rowTestId="user-row" mobileTitle={(u) => u.full_name} />
      {inviting && <InviteDialog onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }} />}
    </div>
  );
}
